import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteData';
import logoImg from '../assets/rare-locals-logo.webp';
import { BRAND } from '../brand';

const LEGAL_LINKS = [
  { label: 'Privacy Policy', slug: 'privacy-policy' },
  { label: 'Shipping Policy', slug: 'shipping-policy' },
  { label: 'Terms of Service', slug: 'terms-of-service' },
  { label: 'Cancellation Policy', slug: 'cancellation-policy' },
  { label: 'Return Policy', slug: 'return-policy' },
  { label: 'Refund Policy', slug: 'refund-policy' },
];

const ColHeading = ({ children }) => (
  <h4 className="text-brand-gold text-[11px] tracking-[0.28em] uppercase font-semibold mb-5">
    {children}
  </h4>
);

export default function Footer() {
  const site = useSiteSettings();
  const social = site?.social || {};
  const socials = [
    { key: 'instagram', label: 'Instagram', href: social.instagram, Icon: Instagram },
    { key: 'facebook', label: 'Facebook', href: social.facebook, Icon: Facebook },
    { key: 'linkedin', label: 'LinkedIn', href: social.linkedin, Icon: Linkedin },
  ].filter((s) => s.href && /^https:\/\//.test(s.href));

  return (
    <footer className="bg-brand-dark text-brand-gold-light mt-16" data-testid="site-footer">
      <div className="gold-foil-rule" aria-hidden="true" />
      {/* Main columns */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14 sm:py-16">
        <div className="grid grid-cols-12 gap-x-6 md:gap-x-10 gap-y-12">
          {/* Brand column */}
          <div className="col-span-12 lg:col-span-3 text-center md:text-left">
            <Link to="/" className="inline-flex flex-col md:flex-row md:items-center gap-3 md:gap-4 group" aria-label="Rare Locals home">
              <img
                src={logoImg}
                alt="Rare Locals"
                width={112}
                height={112}
                className="w-28 h-28 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain flex-shrink-0 mx-auto md:mx-0"
                loading="lazy"
                decoding="async"
              />
              <div className="flex flex-col leading-tight items-center md:items-start">
                <span className="font-serif-brand font-semibold tracking-[0.14em] text-brand-gold text-xl sm:text-2xl">
                  RARE LOCALS
                </span>
                <span className="uppercase tracking-[0.18em] text-brand-gold-light/60 text-[10px] mt-1.5">
                  Rare in nature. Local at heart.
                </span>
              </div>
            </Link>

            <p className="mt-6 text-sm leading-relaxed text-brand-gold-light/65 max-w-sm mx-auto md:mx-0">
              Small-batch, farm-first coffee, tea and dehydrated goods —
              sourced from local growers, delivered fresh to your door.
            </p>

            {socials.length > 0 && (
              <div className="flex items-center gap-2.5 mt-7 justify-center md:justify-start" role="list">
                {socials.map(({ key, label, href, Icon }) => (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    role="listitem"
                    className="w-9 h-9 border border-brand-gold/30 rounded-full flex items-center justify-center hover:bg-brand-gold hover:text-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold transition-colors"
                    aria-label={`Rare Locals on ${label}`}
                  >
                    <Icon size={15} aria-hidden="true" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Shop */}
          <div className="col-span-6 md:col-span-4 lg:col-span-2 text-center md:text-left">
            <ColHeading>Shop</ColHeading>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/shop" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Shop All Products</Link></li>
              <li><Link to="/shop/dehydrated-fruits" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Dehydrated Fruits</Link></li>
              <li><Link to="/shop/pet-dehydrated-treats" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Pet Treats</Link></li>
              <li><Link to="/shop/coffee" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Coffee</Link></li>
              <li><Link to="/shop/tea" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Tea</Link></li>
              <li><Link to="/shop/spices" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Spices</Link></li>
            </ul>
          </div>

          {/* B2B */}
          <div className="col-span-6 md:col-span-4 lg:col-span-2 text-center md:text-left" data-testid="footer-b2b">
            <ColHeading>Bulk &amp; B2B</ColHeading>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/b2b" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Bulk &amp; B2B Supply</Link></li>
              <li><Link to="/institution-sales/catalogue" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">B2B Catalogue</Link></li>
              <li><Link to="/institution-sales/plant" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Our Manufacturing Plant</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div className="col-span-12 md:col-span-4 lg:col-span-2 text-center md:text-left">
            <ColHeading>Company</ColHeading>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/about" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Our Story</Link></li>
              <li><Link to="/blog" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Journal / Blog</Link></li>
              <li><Link to="/contact" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Contact</Link></li>
              <li><Link to="/company-registrations" data-testid="footer-company-registrations" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">Company &amp; Registrations</Link></li>
              <li><Link to="/wishlist" className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">My Wishlist</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-12 lg:col-span-3 text-center md:text-left">
            <ColHeading>Contact</ColHeading>
            <ul className="space-y-3 text-sm inline-block md:block text-left">
              <li className="flex items-start gap-3">
                <MapPin size={15} className="mt-0.5 text-brand-gold flex-shrink-0" aria-hidden="true" />
                <span className="text-brand-gold-light/85 leading-relaxed">
                  {BRAND.contact.address_line1}<br />
                  {BRAND.contact.address_line2}<br />
                  {BRAND.contact.address_state}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={15} className="text-brand-gold flex-shrink-0" aria-hidden="true" />
                <a href={`tel:${BRAND.contact.phone_tel}`} className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">
                  {BRAND.contact.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={15} className="text-brand-gold flex-shrink-0" aria-hidden="true" />
                <a href={`mailto:${BRAND.contact.email}`} className="text-brand-gold-light/85 hover:text-brand-gold transition-colors">
                  {BRAND.contact.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock size={15} className="text-brand-gold flex-shrink-0" aria-hidden="true" />
                <span className="text-brand-gold-light/85">{BRAND.contact.hours}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Legal entity strip */}
      <div className="gold-foil-top">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-5 text-xs text-brand-gold-light/55 leading-relaxed text-center md:text-left">
          <p data-testid="footer-legal-line">
            <span className="text-brand-gold-light/75 font-medium">Rare Locals</span> is operated by{' '}
            <span className="text-brand-gold-light/80">{BRAND.legal_name}</span>
            <span className="mx-1.5 text-brand-gold/50">|</span>CIN: <span className="font-mono text-brand-gold-light/75">{BRAND.registrations.cin}</span>
            <span className="mx-1.5 text-brand-gold/50">|</span>GSTIN: <span className="font-mono text-brand-gold-light/75">{BRAND.registrations.gstin}</span>
            <span className="mx-1.5 text-brand-gold/50">|</span>DPIIT-recognized Startup No. <span className="font-mono text-brand-gold-light/75">{BRAND.registrations.dpiit}</span>
            <span className="mx-1.5 text-brand-gold/50">|</span><Link to="/company-registrations" className="underline underline-offset-2 hover:text-brand-gold">Verify</Link>
          </p>
          <p className="mt-1.5">
            Registered office: {BRAND.contact.address_line1}, {BRAND.contact.address_line2}, {BRAND.contact.address_state}.
          </p>
        </div>
      </div>

      {/* Bottom bar: legal links + copyright, on the same line at desktop */}
      <div className="gold-foil-top">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-5 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-center md:text-left text-xs text-brand-gold-light/55 whitespace-nowrap">
            © {new Date().getFullYear()} Rare Locals. All rights reserved.
          </div>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-2 sm:flex sm:flex-wrap sm:items-center sm:justify-center md:justify-end sm:gap-x-6">
            {LEGAL_LINKS.map(({ label, slug }) => (
              <li key={slug} className="text-center sm:text-left">
                <Link
                  to={`/legal/${slug}`}
                  className="text-brand-gold-light/75 hover:text-brand-gold transition-colors text-xs sm:text-[13px] whitespace-nowrap"
                  data-testid={`footer-legal-${slug}`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
